import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Col, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import { getShopMenu } from "../../../slices/thunks";
import { callService } from "../../../helpers/backend_helper";
import { toVND } from "../../../helpers/helpers";
import DetailMenuOffcanvas   from "../../../Components/Common/DetailMenuOffcanvas";
import CartIcon  from "../../../Components/Common/CartIcon";
import AlertModal from "../../../Components/Common/AlertModal";
import { DEFAULT_PRODUCT_IMAGE } from "../../../constants/images";
import { isMenuAvailable } from "../../../helpers/menuAvailability";
import logo from "../../../assets/img/core-img/logo-small.png";

const resolveShopImageUrl = (imagePath?: string | null): string | null => {
    if (!imagePath || typeof imagePath !== "string") {
        return null;
    }

    const normalizedPath = imagePath.trim();
    if (!normalizedPath) {
        return null;
    }

    if (/^(?:https?:)?\/\//.test(normalizedPath) || normalizedPath.startsWith("data:") || normalizedPath.startsWith("blob:")) {
        return normalizedPath;
    }

    const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const apiBaseUrl = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");
    const apiOrigin = apiBaseUrl
        ? new URL(apiBaseUrl, fallbackOrigin || "http://localhost").origin
        : fallbackOrigin;
    const cleanedPath = normalizedPath.replace(/^\/+/, "");

    if (cleanedPath.startsWith("uploads/")) {
        return `${apiOrigin}/storage/${cleanedPath}`;
    }

    return `${apiOrigin}/${cleanedPath}`;
};

interface MenuProps {
    onItemAdded?: () => void;
    onCartClick?: () => void;
    shopSlug?: string | null;
    table?: string | null;
    customerPosition?: { latitude: number | null; longitude: number | null; accuracy: number | null };
    gpsError?: string | null;
}

const Menu: React.FC<MenuProps> = ({ onItemAdded, onCartClick, shopSlug, table, customerPosition, gpsError }) => {
    const dispatch: any = useDispatch();
    const { shopMenu } = useSelector((state: any) => state.ShopMenu);

    useEffect(() => {
        if (!shopSlug) {
            return;
        }
        dispatch(getShopMenu({ slug: shopSlug }));
    }, [dispatch, shopSlug]);

    const [cateMenuTab, setcateMenuTab] = useState('0');
    const shopInfo = shopMenu?.shop ?? shopMenu;
    const hasImage = shopInfo?.hasImage;
    const shopImage = hasImage ? (resolveShopImageUrl(shopInfo?.image || shopInfo?.avatar) || logo) : logo;
    const toggleCateMenuTab = (tab : any) => {
        if (cateMenuTab !== tab) {
            setcateMenuTab(tab);
        }
    };

    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [isCallingService, setIsCallingService] = useState(false);
    const [alertModalOpen, setAlertModalOpen] = useState(false);
    const [alertModalType, setAlertModalType] = useState<"success" | "danger" | "warning" | "info">("info");
    const [alertModalTitle, setAlertModalTitle] = useState<string | undefined>(undefined);
    const [alertModalMessage, setAlertModalMessage] = useState<string>("");
    const [nextCallAvailableAt, setNextCallAvailableAt] = useState<number | null>(null);

    const [isWifiModalOpen, setIsWifiModalOpen] = useState(false);
    const [wifiCopied, setWifiCopied] = useState(false);

    const handleCopyWifiPassword = async () => {
        const password = shopInfo?.wifi_password;
        if (!password) {
            return;
        }

        try {
            await navigator.clipboard.writeText(password);
            setWifiCopied(true);
            window.setTimeout(() => setWifiCopied(false), 2000);
        } catch (error) {
            console.error(error);
            openAlertModal("Không thể sao chép mật khẩu Wi-Fi. Vui lòng thử lại.", "warning");
        }
    };

    const handleSelectedProduct = (product: any) => {
        setSelectedProduct(product);
    }

    const closeDetailMenu = () => {
        setSelectedProduct(null);
    }

    const openAlertModal = (message: string, type: "success" | "danger" | "warning" | "info", title?: string) => {
        setAlertModalMessage(message);
        setAlertModalType(type);
        setAlertModalTitle(title);
        setAlertModalOpen(true);
    };

    const handleCloseAlertModal = () => {
        setAlertModalOpen(false);
    };

    useEffect(() => {
        const storedTimestamp = window.localStorage.getItem("serviceCallCooldownUntil");
        if (storedTimestamp) {
            const parsed = Number(storedTimestamp);
            if (!Number.isNaN(parsed) && parsed > Date.now()) {
                setNextCallAvailableAt(parsed);
            }
        }
    }, []);

    useEffect(() => {
        if (!nextCallAvailableAt) {
            return;
        }
        const timeout = window.setTimeout(() => {
            setNextCallAvailableAt(null);
            window.localStorage.removeItem("serviceCallCooldownUntil");
        }, Math.max(nextCallAvailableAt - Date.now(), 0));

        return () => window.clearTimeout(timeout);
    }, [nextCallAvailableAt]);

    const handleCallService = async () => {
        if (isCallingService) {
            return;
        }

        if (nextCallAvailableAt && nextCallAvailableAt > Date.now()) {
            const remainingSeconds = Math.ceil((nextCallAvailableAt - Date.now()) / 1000);
            openAlertModal(
                `Vui lòng đợi ${remainingSeconds} giây trước khi gọi phục vụ tiếp.`,
                "warning",
                "Bạn vừa gọi phục vụ"
            );
            return;
        }

        if (!shopInfo?.id) {
            openAlertModal("Không xác định được cửa hàng để gọi phục vụ.", "danger");
            return;
        }

        const tableId = table ? Number(table) : NaN;
        if (!Number.isInteger(tableId) || tableId < 1) {
            openAlertModal("Mã QR không gắn với bàn hợp lệ. Vui lòng quét lại mã QR tại bàn.", "danger", "Không thể gọi phục vụ");
            return;
        }

        if (gpsError) {
            openAlertModal(gpsError, "warning", "Không thể gọi phục vụ");
            return;
        }

        if (customerPosition?.latitude === null || customerPosition?.longitude === null) {
            openAlertModal("Đang xác định vị trí của bạn. Vui lòng thử lại sau.", "warning", "Không thể gọi phục vụ");
            return;
        }

        setIsCallingService(true);

        try {
            await callService({
                shop_id: shopInfo.id,
                table_id: tableId,
                customer_latitude: customerPosition?.latitude ?? undefined,
                customer_longitude: customerPosition?.longitude ?? undefined,
                customer_accuracy: customerPosition?.accuracy ?? undefined,
            });
            openAlertModal("Đã gửi yêu cầu gọi phục vụ.", "success");
            const cooldownUntil = Date.now() + 60 * 1000;
            setNextCallAvailableAt(cooldownUntil);
            window.localStorage.setItem("serviceCallCooldownUntil", String(cooldownUntil));
        } catch (error) {
            console.error(error);
            const message = typeof error === 'string' ? error : (error as any)?.message ?? "Không thể gửi yêu cầu gọi phục vụ. Vui lòng thử lại.";
            openAlertModal(message, "danger");
        } finally {
            setIsCallingService(false);
        }
    };

    const isCooldownActive = !!nextCallAvailableAt && nextCallAvailableAt > Date.now();

    return (
        <div
            className="web-menu-page d-flex flex-column overflow-hidden"
            style={{
                height: "calc(100dvh - 3rem - env(safe-area-inset-bottom, 0px))",
                minHeight: 0,
                overscrollBehavior: "none",
            }}
        >
            <CartIcon onClick={onCartClick} />
            <AlertModal
                isOpen={alertModalOpen}
                title={alertModalTitle}
                message={alertModalMessage}
                type={alertModalType}
                onClose={handleCloseAlertModal}
            />

            <Modal isOpen={isWifiModalOpen} toggle={() => setIsWifiModalOpen(false)} centered>
                <ModalHeader toggle={() => setIsWifiModalOpen(false)}>Wi-Fi của quán</ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <small className="text-muted d-block">Tên Wi-Fi</small>
                        <strong>{shopInfo?.wifi_name || "Chưa cập nhật"}</strong>
                    </div>
                    <div className="mb-0">
                        <small className="text-muted d-block">Mật khẩu</small>
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <strong>{shopInfo?.wifi_password || "Chưa cập nhật"}</strong>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleCopyWifiPassword}
                                disabled={!shopInfo?.wifi_password}
                            >
                                <i className="mdi mdi-content-copy me-1"></i>
                                {wifiCopied ? "Đã copy" : "Copy"}
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            {selectedProduct && (
                <DetailMenuOffcanvas
                    menuId={selectedProduct.id}
                    isOpen={selectedProduct !== null}
                    onClose={closeDetailMenu}
                    onAddedToCart={onItemAdded}
                />
            )}

            <div className="vendor-details-wrap bg-img bg-overlay py-4 position-relative z-3 flex-shrink-0">
                <div className="container">
                    <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="d-flex align-items-start flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="vendor-profile shadow me-3 flex-shrink-0">
                                <figure className="m-0">
                                    <img src={shopImage} alt={shopInfo?.name || "Kiosk"}/>
                                </figure>
                            </div>
                            <div className="vendor-info" style={{ minWidth: 0 }}>
                                <h6 className="mb-1 vendor-title text-white text-break">{shopInfo?.name}</h6>
                                <p className="mb-1 text-white text-break">
                                    <i className="mdi mdi-map-marker me-1"></i>{shopInfo?.address}
                                </p>
                                <p className="mb-1 text-white text-break">
                                    <i className="mdi mdi-phone me-1"></i>{shopInfo?.phone}
                                </p>
                            </div>
                        </div>
                        <div className="d-flex flex-column align-items-stretch gap-2 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-light text-primary text-nowrap"
                                onClick={handleCallService}
                                disabled={isCallingService || isCooldownActive}
                            >
                                <i className="mdi mdi-bell-ring me-1"></i>
                                {isCallingService ? "Đang gọi..." : "Gọi phục vụ"}
                            </button>
                            <button
                                type="button"
                                className="btn text-white text-nowrap"
                                title="Xem Wi-Fi"
                                onClick={() => setIsWifiModalOpen(true)}
                            >
                                <i className="mdi mdi-wifi me-1"></i>
                                Wi-Fi
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="tab-content flex-fill overflow-hidden" style={{ minHeight: 0 }}>
                <div className="h-100">
                    <Row className="h-100 g-0" style={{ minHeight: 0 }}>
                        <Col xs={3} className="h-100 overflow-hidden ps-0 pe-0 py-2">
                            <Nav
                                tabs
                                className="nav nav-tabs nav-tabs-left nav-border-top nav-border-top-primary h-100 overflow-auto align-content-start pb-0"
                                style={{
                                    overscrollBehaviorY: "contain",
                                    WebkitOverflowScrolling: "touch",
                                }}
                            >
                                {
                                    shopMenu?.categories && shopMenu.categories.map((item: any, index: number) => (
                                        <NavItem key={index}>
                                            <NavLink
                                                className={classnames("cursor-pointer", {active: cateMenuTab === index.toString(),})}
                                                onClick={() => {
                                                    toggleCateMenuTab(index.toString());
                                                }}>
                                                {item.name}
                                            </NavLink>
                                        </NavItem>
                                    ))
                                }
                            </Nav>
                        </Col>

                        <Col xs={9} className="h-100 overflow-hidden ps-0 pe-0">
                            <div
                                className="container h-100 overflow-auto py-1"
                                style={{
                                    minHeight: 0,
                                    overscrollBehaviorY: "contain",
                                    WebkitOverflowScrolling: "touch",
                                }}
                            >
                                <TabContent activeTab={cateMenuTab} className="text-muted">
                                    {
                                        shopMenu?.categories && shopMenu.categories.map((item: any, index: number) => (
                                            <TabPane tabId={index.toString()} key={index}>
                                                {hasImage ? (
                                                    <div className="row g-2 pt-2 pb-0 rtl-flex-d-row-r featured-product-card-custom">
                                                        {item?.menus && item.menus.map((product: any, index: number) => {
                                                            const available = isMenuAvailable(product);
                                                            return (
                                                                <div className="col-6" key={index}>
                                                                    <div
                                                                        className={`card product-card ${!available ? 'disabled' : ''}`}
                                                                        onClick={available ? () => handleSelectedProduct(product) : undefined}
                                                                        role="button"
                                                                        aria-disabled={!available}
                                                                        style={{ cursor: available ? 'pointer' : 'not-allowed', opacity: available ? 1 : 0.6 }}
                                                                    >
                                                                        <div className="card-body position-relative">
                                                                            {!available && (
                                                                                <span className="badge bg-secondary position-absolute top-0 end-0 m-2">
                                                                                    Hết món
                                                                                </span>
                                                                            )}
                                                                            <div className="product-thumbnail-side">
                                                                                <div className="product-thumbnail d-block">
                                                                                    <img
                                                                                        src={product.image || DEFAULT_PRODUCT_IMAGE}
                                                                                        alt={product.name ?? "menu"}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="product-description">
                                                                                <p className="product-title d-block mb-1">
                                                                                    {product.name}
                                                                                </p>
                                                                                <p className="sale-price">{toVND(product.price)}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="cart-table card mb-0 menu-list-plain">
                                                        <div className="table-responsive card-body">
                                                            <table className="table mb-0">
                                                                <tbody>
                                                                {item?.menus && item.menus.map((product: any, index: number) => {
                                                                    const available = isMenuAvailable(product);
                                                                    return (
                                                                        <tr key={index} style={{ opacity: available ? 1 : 0.6 }}>
                                                                            <td>
                                                                                <div className="d-flex align-items-center justify-content-between gap-2">
                                                                                    <a
                                                                                        className="product-title mb-0"
                                                                                        href="#"
                                                                                        onClick={(event) => {
                                                                                            event.preventDefault();
                                                                                            if (available) {
                                                                                                handleSelectedProduct(product);
                                                                                            }
                                                                                        }}
                                                                                        role="button"
                                                                                        aria-disabled={!available}
                                                                                        style={{ cursor: available ? 'pointer' : 'not-allowed' }}
                                                                                    >
                                                                                        {product.name}
                                                                                    </a>
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <span className="menu-list-price">{toVND(product.price)}</span>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-sm btn-primary"
                                                                                            onClick={() => handleSelectedProduct(product)}
                                                                                            disabled={!available}
                                                                                            aria-label={`Thêm ${product.name}`}
                                                                                        >
                                                                                            +
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                                {!available && (
                                                                                    <span className="badge bg-secondary ms-2">
                                                                                            Hết món
                                                                                        </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </TabPane>
                                        ))
                                    }
                                </TabContent>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default Menu;
